/**
 * FmMapTools — floating glass map-tools pill (position:absolute top:14 left:14).
 * Replaces removed docked FmIconRail.
 * View-scoped: renders null when no panels registered.
 * Buttons: aria-current="true" when panel active.
 * data-testid="maptools"; buttons data-testid={`maptools__btn-${panel.key}`}.
 */
import React from "react";
import {
  AimOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useLanguage } from "@/i18n";
import { FmTooltip } from "@/components/ui";
import { useOptionalFmMapViewport } from "@/components/ui/FmMapViewportContext";
import { useFmContextPanel } from "@/contexts/fmContextPanelInternals";
import "./FmMapTools.css";

export interface FmMapToolsProps {
  hoverPeek?: boolean;
  fitLabel?: string;
  actions?: readonly {
    id: string;
    label: string;
    tool: "create" | "refresh" | "search";
    disabledReason?: string;
    onSelect: () => void;
  }[];
}

const ACTION_ICONS = {
  create: <PlusOutlined />,
  refresh: <ReloadOutlined />,
  search: <SearchOutlined />,
} as const;

const PANEL_FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function enterExpandedPanel(
  event: React.KeyboardEvent<HTMLButtonElement>,
  panelKey: string,
  isActive: boolean,
): void {
  if (event.key !== "Tab" || event.shiftKey || !isActive) return;
  const firstControl = document
    .getElementById(`fm-context-panel-${panelKey}`)
    ?.querySelector<HTMLElement>(PANEL_FOCUSABLE);
  if (!firstControl) return;
  event.preventDefault();
  firstControl.focus();
}

const FmMapTools: React.FC<FmMapToolsProps> = ({
  actions = [],
  fitLabel,
  hoverPeek = false,
}) => {
  const { t } = useLanguage();
  const controller = useOptionalFmMapViewport()?.controller ?? null;
  const {
    panels,
    activePanel,
    isPanelOpen,
    togglePanel,
    peekPanel,
    scheduleClosePeekedPanel,
    cancelPeekClose,
  } = useFmContextPanel();
  if (panels.length === 0 && actions.length === 0) return null;
  return (
    <div
      className="maptools"
      data-testid="maptools"
      aria-label={t.shell?.panel?.mapToolsAriaLabel}
      onWheel={(e) => e.stopPropagation()}
      onMouseEnter={hoverPeek ? cancelPeekClose : undefined}
      onMouseLeave={hoverPeek ? scheduleClosePeekedPanel : undefined}
      onFocus={hoverPeek ? cancelPeekClose : undefined}
      onBlur={hoverPeek ? scheduleClosePeekedPanel : undefined}
    >
      {fitLabel ? (
        <FmTooltip title={fitLabel} placement="right">
          <button
            type="button"
            className="maptools__btn"
            aria-label={fitLabel}
            disabled={!controller}
            data-testid="maptools__btn-fit"
            onClick={controller?.fit}
          >
            <AimOutlined />
          </button>
        </FmTooltip>
      ) : null}
      {actions.map((action) => (
        <FmTooltip
          key={action.id}
          title={action.disabledReason ?? action.label}
          placement="right"
        >
          <button
            type="button"
            className="maptools__btn"
            aria-label={action.label}
            disabled={Boolean(action.disabledReason)}
            data-testid={`maptools__action-${action.id}`}
            onClick={action.onSelect}
          >
            {ACTION_ICONS[action.tool]}
          </button>
        </FmTooltip>
      ))}
      {panels
        .filter((panel) => panel.visibleInMapTools !== false)
        .map((panel) => {
          const isActive = isPanelOpen && activePanel === panel.key;
          return (
            <FmTooltip key={panel.key} title={panel.tooltip} placement="right">
              <button
                type="button"
                className="maptools__btn"
                aria-label={panel.tooltip}
                aria-current={isActive ? "true" : undefined}
                aria-expanded={isActive}
                aria-controls={`fm-context-panel-${panel.key}`}
                data-testid={`maptools__btn-${panel.key}`}
                onMouseEnter={
                  hoverPeek ? () => peekPanel(panel.key) : undefined
                }
                onFocus={hoverPeek ? () => peekPanel(panel.key) : undefined}
                onKeyDown={(event) =>
                  enterExpandedPanel(event, panel.key, isActive)
                }
                onClick={() => togglePanel(panel.key)}
              >
                {panel.icon}
              </button>
            </FmTooltip>
          );
        })}
    </div>
  );
};

export default FmMapTools;
